// R15 — WHAT A FILE PICKER MAY OFFER, AND IT IS THE SAME LAW THE SERVER ENFORCES.
//
// A file picker makes a promise. `accept="…"` is what the operating system shows the member as
// choosable, and every one of those types had better survive the upload, because the thing that
// actually decides is 200 lines away in `server/uploads.js` — a magic-byte sniffer that trusts
// nothing the client says about a file. Those two lists were written by hand, in different files,
// and nothing crossed between them. The failure that costs her something is not abstract: the
// picker offers a type, she waits out a full upload on a phone, and the answer is "that file is
// not one I can open."
//
// So this module is the single declaration, imported by BOTH sides — the client to compose the
// `accept` attribute, `server/uploads.js` to decide what it stores — and `scripts/r15-upload-accept.mjs`
// pushes real bytes for every offered type through the server's real `prepareAttachment` to hold the
// two together by behaviour rather than by a copy of the list typed into a fixture.
//
// Imports nothing, so both a `.mjs` gate and the bundle can load it.

// The image types the VENDOR itself accepts (Anthropic Messages API). These are the only media
// types an image attachment is ever stored as.
export const VENDOR_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

// Accepted, but never stored as themselves: an iPhone hands over HEIC by default and the vendor
// will not take it, so these are converted to JPEG on arrival (or refused honestly when the
// installed sharp cannot decode them). They belong in a picker — a member should be able to
// choose the photo her phone actually made — and they never reach the vendor under these names.
export const CONVERTED_IMAGE_TYPES = ["image/heic", "image/avif"];

// A PICKER NAMES A TYPE THE WAY THE OPERATING SYSTEM LABELS THE FILE; the sniffer names the family
// it really belongs to. HEIF and HEIC are one container with a dozen brand codes (`mif1`, `msf1`,
// `heix`, …) and `sniffMediaType` calls all of them `image/heic`, so `image/heif` is offered — it
// is what a member's own file is labelled — and resolves to the sniffed name here. Written down
// rather than left implicit, because otherwise the both-directions check reads it as drift.
export const PICKER_TYPE_ALIASES = { "image/heif": "image/heic" };

// Documents read by the model directly (PDF) or by extracting their words first (DOCX).
export const DOCUMENT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

// Plain text has no magic bytes, so the server decides it by content (valid UTF-8, no control
// bytes). A picker cannot express "is it text" — it can only name extensions, so it names these.
export const TEXT_EXTENSIONS = [".txt", ".md", ".csv"];

// The DOCX media type is far too long to be readable in an `accept` attribute and every OS maps
// the extension anyway, so the picker names the extension and the server still sniffs the bytes.
const DOCX_EXTENSION = ".docx";

// WHAT ASK LALA'S ATTACHMENT BUTTON OFFERS. Every entry here is something `prepareAttachment`
// really does accept, proved by running it, not by matching this list against a copy of itself.
export function attachmentAccept() {
  return [
    ...VENDOR_IMAGE_TYPES,
    ...CONVERTED_IMAGE_TYPES,
    ...Object.keys(PICKER_TYPE_ALIASES),
    "application/pdf",
    ...TEXT_EXTENSIONS,
    DOCX_EXTENSION,
  ].join(",");
}

// WHAT A PHOTO-ONLY PICKER OFFERS, AND THE WIDTH IS DELIBERATE. The palm reading, the tarot spread
// photo and the Living Room's pictures all open a camera or a photo library on a phone, and
// `image/*` is the string every mobile browser is known to handle for that. Narrowing it to a
// list would buy a slightly more honest picker and risk the one path a member takes with a phone
// in her hand, so it stays wide on purpose. It is SAFE to stay wide because these paths re-encode
// through a canvas before upload (`src/photoUpload.js`), so what the server receives is a JPEG
// whatever was chosen; and when that decode falls back to the original bytes, the sniffer is still
// the gate. Recorded here with its reason so the fixture holds a decision rather than an accident.
export const PHOTO_ACCEPT_IS_WIDE_BECAUSE =
  "a phone's camera and photo library are opened by image/*; the canvas re-encode and the server's sniffer are the real gates";
export function photoAccept() {
  return "image/*";
}

// Every media type the server will accept in some form — native, or after conversion. The fixture
// holds this to the sniffer in BOTH directions: nothing offered may be refused, and nothing
// accepted may go unoffered (a capability nobody can reach is R11's dead branch, inverted).
export function acceptedMediaTypes() {
  return [...VENDOR_IMAGE_TYPES, ...CONVERTED_IMAGE_TYPES, ...DOCUMENT_TYPES];
}
